/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import { AZHost } from 'object-types';

import {
    IAZHost,
    INsxtTransportnode,
} from 'generated-types';

import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

// NSX Hosts API URL.
const NSXT_TRANSPORTNODES_URL = '/api/nsxt/transportnodes';

/**
 * @description
 *
 *   Az Host Message Item.
 *
 * @author Sarthak kapoor
 */
export class AZHostConfigItem extends withFullModalMixin(MessageItem)<IAZHost> {
    protected static ngDependencies = [HTTP_WRAPPER_TOKEN];

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: AZHost,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getNgDependency(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();
    }

    /**
     * Fetch NSX Trasnport Nodes list.
     */
    public async fetchNsxTransportNodesList(
        vcenterId: string,
        cloudId: string,
        invokedFromParentModal = false,
    ): Promise<INsxtTransportnode[]> {
        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: NSXT_TRANSPORTNODES_URL,
                method: HttpMethod.POST,
                data: {
                    cloud_uuid: cloudId,
                    vcenter_uuid: vcenterId,
                },
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { nsxt_transportnodes: nsxtTransportnodes = [] } = resource;

            return nsxtTransportnodes;
        } catch (error) {
            if (invokedFromParentModal) {
                throw error;
            }

            this.errors = error.data;
        } finally {
            this.busy = false;
        }
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            AzHostModalComponent,
        } = await import(
            /* webpackChunkName: "availability-zone-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/availability-zone-modal/az-host-modal/az-host-modal.component'
        );

        return AzHostModalComponent as Type<Component>;
    }
}
